import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue,
  SelectGroup,
  SelectLabel
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  FlaskConical, 
  Search, 
  Play, 
  Send, 
  Calendar, 
  CheckCircle, 
  XCircle,
  AlertCircle,
  Eye,
  RefreshCw,
  User,
  FileText,
  Zap,
  Clock,
  Webhook,
  Users
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEmailPlayground, JOB_OPTIONS, PlaygroundUser, PlaygroundMode, getJobsByCategory } from '@/hooks/useEmailPlayground';
import { useEmailTemplates } from '@/hooks/useEmailTemplates';
import TemplateVariableEditor, { extractVariablesFromHtml } from './TemplateVariableEditor';

const EmailPlayground = () => {
  const {
    mode,
    setMode,
    selectedJob,
    setSelectedJob,
    selectedUser,
    setSelectedUser,
    sourceUser,
    setSourceUser,
    selectedTemplateKey,
    setSelectedTemplateKey,
    customVariables,
    setCustomVariables,
    updateCustomVariable,
    result,
    isSimulating,
    isScheduling,
    simulate,
    scheduleEmail,
    reset,
  } = useEmailPlayground();

  const [userSearch, setUserSearch] = useState('');
  const [sourceUserSearch, setSourceUserSearch] = useState('');
  const { templates } = useEmailTemplates();

  // Fetch users for dropdown
  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ['admin-users-for-playground', userSearch],
    queryFn: async () => {
      let query = supabase
        .from('profiles')
        .select('id, username, plan_type')
        .eq('onboarding_completed', true)
        .order('username', { ascending: true })
        .limit(50);

      if (userSearch) {
        query = query.ilike('username', `%${userSearch}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as PlaygroundUser[];
    },
    staleTime: 30000,
  });

  // Fetch source users (same query but separate search)
  const { data: sourceUsers, isLoading: sourceUsersLoading } = useQuery({
    queryKey: ['admin-source-users-for-playground', sourceUserSearch],
    queryFn: async () => {
      let query = supabase
        .from('profiles')
        .select('id, username, plan_type')
        .eq('onboarding_completed', true)
        .order('username', { ascending: true })
        .limit(50);

      if (sourceUserSearch) {
        query = query.ilike('username', `%${sourceUserSearch}%`);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as PlaygroundUser[];
    },
    staleTime: 30000,
  });

  const handleUserSelect = (userId: string) => {
    const user = users?.find(u => u.id === userId);
    if (user) {
      setSelectedUser(user);
      reset();
    }
  };

  const handleSourceUserSelect = (userId: string) => {
    const user = sourceUsers?.find(u => u.id === userId);
    if (user) {
      setSourceUser(user);
    }
  };

  const handleModeChange = (newMode: PlaygroundMode) => {
    setMode(newMode);
    reset();
    setSelectedJob('');
    setSelectedTemplateKey('');
    setSourceUser(null);
  };

  const selectedJobInfo = JOB_OPTIONS.find(j => j.value === selectedJob);
  const selectedTemplate = templates?.find(t => t.template_key === selectedTemplateKey);
  const templateVariables = selectedTemplate 
    ? extractVariablesFromHtml(selectedTemplate.html_content)
    : [];

  // Get default variable values based on user
  const getDefaultVariables = (): Record<string, string> => {
    if (!selectedUser) return {};
    return {
      UserName: selectedUser.username || 'Usuário',
      Name: selectedUser.username || 'Usuário',
      Username: selectedUser.username || '',
      Email: selectedUser.email || '',
      SiteUrl: 'https://interlaced.app',
      SiteURL: 'https://interlaced.app',
      UnsubscribeUrl: `https://interlaced.app/unsubscribe?uid=${selectedUser.id}`,
      ConfirmationURL: 'https://interlaced.app/confirm?token=PREVIEW',
      Token: '123456',
      TokenHash: 'preview_hash',
      Date: new Date().toLocaleDateString('pt-BR'),
      PlanName: selectedUser.plan_type || 'Free',
    };
  };

  const handleResetVariables = () => {
    setCustomVariables(getDefaultVariables());
  };

  // Initialize variables when template or user changes
  React.useEffect(() => {
    if (mode === 'templates' && selectedTemplate && selectedUser) {
      const defaults = getDefaultVariables();
      setCustomVariables(prev => {
        const merged = { ...defaults };
        for (const key of Object.keys(prev)) {
          if (prev[key]) merged[key] = prev[key];
        }
        return merged;
      });
    }
  }, [selectedTemplateKey, selectedUser?.id, mode]);

  // Category icons
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'cron': return <Clock className="h-4 w-4" />;
      case 'trigger': return <Zap className="h-4 w-4" />;
      case 'webhook': return <Webhook className="h-4 w-4" />;
      default: return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-white/5 backdrop-blur-sm border-white/10">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <FlaskConical className="h-5 w-5 text-luxury-pinkBright" />
            Playground de Emails
          </CardTitle>
          <p className="text-white/60 text-sm">
            Simule regras de automação ou visualize qualquer template com dados reais.
          </p>
        </CardHeader>
      </Card>

      {/* Mode Selector */}
      <Card className="bg-white/5 backdrop-blur-sm border-white/10">
        <CardContent className="p-4">
          <Tabs value={mode} onValueChange={(v) => handleModeChange(v as PlaygroundMode)}>
            <TabsList className="bg-white/5 border border-white/10 w-full">
              <TabsTrigger 
                value="automations" 
                className="flex-1 data-[state=active]:bg-luxury-pinkBright data-[state=active]:text-white text-white/60"
              >
                <Zap className="h-4 w-4 mr-2" />
                Automações
              </TabsTrigger>
              <TabsTrigger 
                value="templates" 
                className="flex-1 data-[state=active]:bg-luxury-pinkBright data-[state=active]:text-white text-white/60"
              >
                <FileText className="h-4 w-4 mr-2" />
                Todos os Templates
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </CardContent>
      </Card>

      {/* Selection Area */}
      <Card className="bg-white/5 backdrop-blur-sm border-white/10">
        <CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Job Type or Template Selection */}
            <div className="space-y-2">
              <label className="text-white/60 text-sm">
                {mode === 'automations' ? 'Automação' : 'Template'}
              </label>
              {mode === 'automations' ? (
                <Select 
                  value={selectedJob} 
                  onValueChange={(v) => {
                    setSelectedJob(v as any);
                    setSourceUser(null);
                    reset();
                  }}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue placeholder="Selecione uma automação..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-96">
                    {/* Cron Jobs */}
                    <SelectGroup>
                      <SelectLabel className="flex items-center gap-2 text-white/40">
                        <Clock className="h-3 w-3" />
                        Cron Jobs (Agendados)
                      </SelectLabel>
                      {getJobsByCategory('cron').map((job) => (
                        <SelectItem 
                          key={job.value} 
                          value={job.value}
                          disabled={job.disabled}
                          className={job.disabled ? 'opacity-50' : ''}
                        >
                          <div className="flex flex-col">
                            <span className="flex items-center gap-2">
                              {job.label}
                              {job.disabled && (
                                <Badge variant="outline" className="text-xs bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                                  Desativado
                                </Badge>
                              )}
                            </span>
                            <span className="text-xs text-muted-foreground">{job.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectGroup>

                    {/* Triggers */}
                    <SelectGroup>
                      <SelectLabel className="flex items-center gap-2 text-white/40 mt-2">
                        <Zap className="h-3 w-3" />
                        Triggers (Eventos)
                      </SelectLabel>
                      {getJobsByCategory('trigger').map((job) => (
                        <SelectItem key={job.value} value={job.value}>
                          <div className="flex flex-col">
                            <span className="flex items-center gap-2">
                              {job.label}
                              {job.requiresSourceUser && (
                                <Users className="h-3 w-3 text-white/40" />
                              )}
                            </span>
                            <span className="text-xs text-muted-foreground">{job.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectGroup>

                    {/* Webhooks */}
                    <SelectGroup>
                      <SelectLabel className="flex items-center gap-2 text-white/40 mt-2">
                        <Webhook className="h-3 w-3" />
                        Webhooks (Pagamentos)
                      </SelectLabel>
                      {getJobsByCategory('webhook').map((job) => (
                        <SelectItem key={job.value} value={job.value}>
                          <div className="flex flex-col">
                            <span>{job.label}</span>
                            <span className="text-xs text-muted-foreground">{job.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              ) : (
                <Select 
                  value={selectedTemplateKey} 
                  onValueChange={(v) => {
                    setSelectedTemplateKey(v);
                    reset();
                  }}
                >
                  <SelectTrigger className="bg-white/5 border-white/10 text-white">
                    <SelectValue placeholder="Selecione um template..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    {templates?.map((t) => (
                      <SelectItem key={t.template_key} value={t.template_key}>
                        <div className="flex items-center gap-2">
                          <span>{t.name}</span>
                          {t.is_active && (
                            <Badge variant="outline" className="text-xs">Ativo</Badge>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* User Selection */}
            <div className="space-y-2">
              <label className="text-white/60 text-sm">Usuário Alvo</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input
                  placeholder="Buscar por username..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="pl-10 bg-white/5 border-white/10 text-white"
                />
              </div>
              <Select 
                value={selectedUser?.id || ''} 
                onValueChange={handleUserSelect}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Selecione um usuário...">
                    {selectedUser && (
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        <span>{selectedUser.username}</span>
                        <Badge variant="outline" className="text-xs">
                          {selectedUser.plan_type}
                        </Badge>
                      </div>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {usersLoading ? (
                    <div className="p-2 text-center text-muted-foreground">Carregando...</div>
                  ) : users?.length === 0 ? (
                    <div className="p-2 text-center text-muted-foreground">Nenhum usuário encontrado</div>
                  ) : (
                    users?.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        <div className="flex items-center gap-2">
                          <span>{user.username}</span>
                          <Badge variant="outline" className="text-xs">
                            {user.plan_type}
                          </Badge>
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Source User Selection (only for triggers that require it) */}
          {mode === 'automations' && selectedJobInfo?.requiresSourceUser && (
            <div className="space-y-2 p-4 bg-white/5 rounded-lg border border-white/10">
              <label className="text-white/60 text-sm flex items-center gap-2">
                <Users className="h-4 w-4" />
                Usuário Fonte (quem realizou a ação)
              </label>
              <p className="text-white/40 text-xs mb-2">
                Para "{selectedJobInfo.label}", selecione quem enviou a curtida/match/mensagem.
              </p>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
                <Input
                  placeholder="Buscar usuário fonte..."
                  value={sourceUserSearch}
                  onChange={(e) => setSourceUserSearch(e.target.value)}
                  className="pl-10 bg-white/5 border-white/10 text-white"
                />
              </div>
              <Select 
                value={sourceUser?.id || ''} 
                onValueChange={handleSourceUserSelect}
              >
                <SelectTrigger className="bg-white/5 border-white/10 text-white">
                  <SelectValue placeholder="Selecione o usuário fonte...">
                    {sourceUser && (
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        <span>{sourceUser.username}</span>
                        <Badge variant="outline" className="text-xs">
                          {sourceUser.plan_type}
                        </Badge>
                      </div>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {sourceUsersLoading ? (
                    <div className="p-2 text-center text-muted-foreground">Carregando...</div>
                  ) : sourceUsers?.length === 0 ? (
                    <div className="p-2 text-center text-muted-foreground">Nenhum usuário encontrado</div>
                  ) : (
                    sourceUsers?.filter(u => u.id !== selectedUser?.id).map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        <div className="flex items-center gap-2">
                          <span>{user.username}</span>
                          <Badge variant="outline" className="text-xs">
                            {user.plan_type}
                          </Badge>
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Template Variables Editor (only in templates mode) */}
          {mode === 'templates' && selectedTemplate && selectedUser && (
            <>
              <Separator className="bg-white/10" />
              <TemplateVariableEditor
                variables={templateVariables}
                values={customVariables}
                onChange={updateCustomVariable}
                onResetToDefaults={handleResetVariables}
              />
            </>
          )}

          {/* Simulate Button */}
          <Button
            onClick={simulate}
            disabled={
              (mode === 'automations' && (!selectedJob || !selectedUser || (selectedJobInfo?.requiresSourceUser && !sourceUser))) ||
              (mode === 'templates' && (!selectedTemplateKey || !selectedUser)) ||
              isSimulating ||
              selectedJobInfo?.disabled
            }
            variant="brand-gradient"
            className="w-full"
          >
            {isSimulating ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                {mode === 'automations' ? 'Simulando...' : 'Gerando Preview...'}
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                {mode === 'automations' ? 'Simular Regras' : 'Gerar Preview'}
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Results Area */}
      {result && (
        <Card className="bg-white/5 backdrop-blur-sm border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              {result.eligible ? (
                <CheckCircle className="h-5 w-5 text-green-400" />
              ) : (
                <XCircle className="h-5 w-5 text-red-400" />
              )}
              {mode === 'automations' ? 'Resultado da Simulação' : 'Preview do Template'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Eligibility Status (only for automations) */}
            {mode === 'automations' && (
              <div className={`p-4 rounded-lg ${result.eligible ? 'bg-green-500/10 border border-green-500/20' : 'bg-red-500/10 border border-red-500/20'}`}>
                <div className="flex items-center gap-2 mb-2">
                  {result.eligible ? (
                    <Badge className="bg-green-500/20 text-green-400">ELEGÍVEL</Badge>
                  ) : (
                    <Badge className="bg-red-500/20 text-red-400">NÃO ELEGÍVEL</Badge>
                  )}
                  {result.templateKey && (
                    <Badge variant="outline" className="text-white/60">
                      {result.templateKey}
                    </Badge>
                  )}
                </div>
                {!result.eligible && result.reason && (
                  <p className="text-white/80 text-sm">
                    <AlertCircle className="inline h-4 w-4 mr-1 text-red-400" />
                    {result.reason}
                  </p>
                )}
              </div>
            )}

            {/* Checks List (only for automations) */}
            {mode === 'automations' && result.checks && result.checks.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-white/80 text-sm font-medium">Critérios verificados:</h4>
                <div className="space-y-1">
                  {result.checks.map((check, index) => (
                    <div 
                      key={index} 
                      className="flex items-center gap-2 text-sm p-2 rounded bg-white/5"
                    >
                      {check.passed ? (
                        <CheckCircle className="h-4 w-4 text-green-400 flex-shrink-0" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
                      )}
                      <span className="text-white/80">{check.name}</span>
                      {check.details && (
                        <span className="text-white/40 ml-auto text-xs">{check.details}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Variables */}
            {result.variables && Object.keys(result.variables).length > 0 && (
              <>
                <Separator className="bg-white/10" />
                <div className="space-y-2">
                  <h4 className="text-white/80 text-sm font-medium">Variáveis aplicadas:</h4>
                  <div className="bg-black/20 rounded-lg p-4 font-mono text-xs space-y-1 max-h-48 overflow-y-auto">
                    {Object.entries(result.variables).map(([key, value]) => (
                      <div key={key} className="flex gap-2">
                        <span className="text-luxury-pinkBright">{key}:</span>
                        <span className="text-white/80 break-all">"{value}"</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Email Preview */}
            {result.previewHtml && (
              <>
                <Separator className="bg-white/10" />
                <div className="space-y-2">
                  <h4 className="text-white/80 text-sm font-medium flex items-center gap-2">
                    <Eye className="h-4 w-4" />
                    Preview do Email
                  </h4>
                  <div className="bg-white rounded-lg overflow-hidden">
                    <iframe
                      srcDoc={result.previewHtml}
                      className="w-full h-[400px] border-0"
                      title="Email Preview"
                      sandbox="allow-same-origin"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Action Buttons */}
            {(result.eligible || mode === 'templates') && result.previewHtml && !result.scheduled && (
              <>
                <Separator className="bg-white/10" />
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={() => scheduleEmail('send_now')}
                    disabled={isScheduling}
                    variant="brand-gradient"
                    className="flex-1"
                  >
                    {isScheduling ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Enviar Agora
                  </Button>
                  {mode === 'automations' && (
                    <Button
                      onClick={() => scheduleEmail('schedule_original')}
                      disabled={isScheduling}
                      variant="outline"
                      className="flex-1 border-white/20 text-white hover:bg-white/10"
                    >
                      <Calendar className="h-4 w-4 mr-2" />
                      Agendar (Data Original)
                    </Button>
                  )}
                </div>
                <p className="text-white/40 text-xs flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  Emails enviados pelo playground são marcados como <code className="bg-white/10 px-1 rounded">source='admin_playground'</code> e não afetam as automações.
                </p>
              </>
            )}

            {/* Scheduled Confirmation */}
            {result.scheduled && (
              <>
                <Separator className="bg-white/10" />
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle className="h-5 w-5 text-green-400" />
                    <span className="text-green-400 font-medium">Email Agendado!</span>
                  </div>
                  <div className="text-white/60 text-sm space-y-1">
                    <p>ID: <code className="bg-white/10 px-1 rounded">{result.scheduledId}</code></p>
                    <p>Agendado para: {new Date(result.scheduledFor!).toLocaleString('pt-BR')}</p>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Card className="bg-white/5 backdrop-blur-sm border-white/10">
        <CardContent className="p-4">
          <div className="text-white/60 text-sm space-y-2">
            <p><strong className="text-white">Como usar:</strong></p>
            {mode === 'automations' ? (
              <ol className="list-decimal list-inside space-y-1">
                <li>Selecione o tipo de automação (Cron/Trigger/Webhook)</li>
                <li>Busque e selecione o usuário alvo</li>
                <li>Para triggers com 👤, selecione também o usuário fonte</li>
                <li>Clique em "Simular Regras" para ver elegibilidade</li>
                <li>Se elegível, visualize o preview e envie ou agende</li>
              </ol>
            ) : (
              <ol className="list-decimal list-inside space-y-1">
                <li>Selecione qualquer template disponível</li>
                <li>Selecione um usuário para popular as variáveis</li>
                <li>Edite as variáveis conforme necessário</li>
                <li>Clique em "Gerar Preview" para visualizar</li>
                <li>Envie o email se desejar</li>
              </ol>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailPlayground;
